<template>
  <div class="entrega-modal-wrapper" v-if="$store.entregaStore.state.currentEntrega">
    <v-dialog
      v-model="$store.entregaStore.state.currentEntrega"
      :max-width="$vuetify.breakpoint.mobile ? '100%' : 1400"
      :fullscreen="$vuetify.breakpoint.mobile"
      scrollable
      persistent
      :retain-focus="false"
      content-class="entrega-dialog"
    >
      <!-- Loading state -->
      <v-card v-if="$store.entregaStore.state.currentEntrega.loading" class="modal-card loading-card">
        <div class="loading-header">
          <div class="modal-brand">
            <span class="brand-icon-dark">
              <v-icon small color="#2D4B95">mdi-file-document-multiple-outline</v-icon>
            </span>
            <span class="brand-label-dark">Cargando entrega</span>
          </div>
          <button class="close-btn-dark" @click="closeModal">
            <v-icon size="18" color="#555">mdi-close</v-icon>
          </button>
        </div>
        <div class="loading-body">
          <v-progress-circular :size="40" :width="3" color="#2D4B95" indeterminate />
          <p class="loading-text">Cargando información…</p>
        </div>
      </v-card>

      <!-- Main card -->
      <v-card v-else class="modal-card">

        <ModalStickyHeader :breakpoint="600">
        <!-- ── TOP BAR ── -->
        <template #topbar>
        <div class="topbar">
          <div class="topbar-left">
            <span class="topbar-icon">
              <v-icon small color="#2D4B95">mdi-file-document-multiple-outline</v-icon>
            </span>
            <div class="topbar-titles">
              <h2 class="topbar-title">
                {{ currentEntrega.title }}
                <span class="topbar-number">#{{ currentEntrega.number }}</span>
              </h2>
              <p class="topbar-rdi" v-if="currentEntrega.task_relation">
                Tarea Asociada #{{ currentEntrega.task_relation.task.number }} —
                {{ currentEntrega.task_relation.task.title }}
                <button class="inline-link-btn"
                  @click="viewTask(currentEntrega.task_relation.task.id, $options.filters.seenByMe(currentEntrega.task_relation.task.seen_by_me))">
                  <v-icon size="13" color="#5E85EF">mdi-arrow-right-bold</v-icon>
                </button>
              </p>
            </div>
          </div>
          <div class="topbar-right">
            <HeaderButtons
              @minimize="minimizeItem"
              @close="closeModal"
              @download-pdf="downloadEntregaPdf"
              @download-attachments="downloadAllAttachments"
            />
          </div>
        </div>
        </template>

        <!-- ── STATUS STRIP ── -->
        <template #status-strip>
        <div class="status-strip">
          <div class="status-strip-pills">
            <span class="pill pill--blue">
              <v-icon size="12" color="#2D4B95">mdi-calendar-month-outline</v-icon>
              {{ currentEntrega.created_at | toDateTime }}
            </span>
            <span class="pill pill--gray">
              <v-icon size="12" color="#555">mdi-clock-time-eight-outline</v-icon>
              Espera: {{ currentEntrega.created_at | waitingTime }}
            </span>
            <span class="pill pill--orange" v-if="currentEntrega.critical_date">
              <v-icon size="12" color="#B45309">mdi-flag-outline</v-icon>
              Fecha crítica: {{ currentEntrega.critical_date | toDate }}
            </span>
            <span class="pill pill--status"
              :style="{ background: getStatusColor(currentEntrega.status) + '18', color: getStatusColor(currentEntrega.status), borderColor: getStatusColor(currentEntrega.status) + '55' }">
              <v-icon size="12" :color="getStatusColor(currentEntrega.status)">mdi-circle-small</v-icon>
              {{ getStatusText() }}
            </span>
            <div class="d-flex align-center" style="gap:5px">
              <v-divider vertical />
              <p class="mb-0 text-primary" style="font-size: 10px;">Visto por:</p>
              <SeenByUsers
                v-model="seenByMenu"
                :seenByUsers="currentEntrega.seen_by || []"
              />
            </div>
          </div>
          <div class="status-strip-actions">
						<BotSummarizeMini v-if="!loading" />
            <v-btn
              v-if="isClosed"
              small
              outlined
              color="#3EBFA3"
              @click="downloadEntregaPdf"
            >
              <v-icon small left>mdi-file-pdf-box</v-icon>
              Descargar PDF
            </v-btn>
            <v-btn
              v-if="!isClosed && (isCreator || checkPermissions(['delete_entregas']))"
              small
              outlined
              color="error"
              @click="startDeleteEntrega"
            >
              <v-icon small left>mdi-delete-outline</v-icon>
              Eliminar
            </v-btn>
          </div>
        </div>
        </template>
        </ModalStickyHeader>

        <!-- ── TWO COLUMN BODY ── -->
        <div class="modal-body" ref="modalBody" :class="{ resizing: isResizing }">

          <!-- LEFT: info panel -->
          <div class="panel-detail" :style="$vuetify.breakpoint.mobile ? {} : { width: leftPanelWidth + '%' }">
            <div class="panel-scroll">

              <!-- Meta grid -->
              <section class="meta-section">

              <!-- Primera fila: 3 columnas (Descripción | Info | Supervisor). Segunda fila: Emplazamiento ancho completo -->
              <section class="content-section">
                <div class="three-col-row three-col-row--equal">
                  <div class="desc-col">
                    <div class="meta-item" v-if="currentEntrega.title">
                      <span class="meta-label">Título</span>
                      <span class="meta-value">{{ currentEntrega.title }}</span>
                    </div>
                    <div class="meta-item" v-if="currentEntrega.project">
                      <span class="meta-label">Proyecto</span>
                      <span class="meta-value">{{ currentEntrega.project.name }}</span>
                    </div>
                    <div class="meta-item" v-if="currentEntrega.status === 3 && currentEntrega.ended_at">
                      <span class="meta-label">
                        <v-icon size="12" color="#3EBFA3">mdi-calendar-check</v-icon> Fecha Finalización
                      </span>
                      <span class="meta-value">{{ currentEntrega.ended_at | toDateTime }}</span>
                    </div>
                    <div class="meta-item" v-if="currentEntrega.status === 3 && currentEntrega.closed_by_user">
                      <span class="meta-label">
                        <v-icon size="12" color="#3EBFA3">mdi-account-check</v-icon> Firmado por
                      </span>
                      <span class="meta-value">{{ currentEntrega.closed_by_user.name }}</span>
                    </div>
                  </div>

                  <div class="info-col">
                    <div class="meta-item" v-if="currentEntrega.protocolo_name">
                      <span class="meta-label">
                        <v-icon size="12" color="#2D4B95">mdi-file-document-outline</v-icon> Protocolo
                      </span>
                      <span class="meta-value">{{ currentEntrega.protocolo_name }}</span>
                    </div>
                    <div class="meta-item" v-if="currentEntrega.nivel">
                      <span class="meta-label">
                        <v-icon size="12" color="#2D4B95">mdi-layers-outline</v-icon> Nivel / Zona
                      </span>
                      <span class="meta-value">{{ currentEntrega.nivel }} — {{ zonaName }}</span>
                    </div>
                    <div class="meta-item" v-if="currentEntrega.ejes_numericos">
                      <span class="meta-label">
                        <v-icon size="12" color="#2D4B95">mdi-axis-arrow</v-icon> Ejes
                      </span>
                      <span class="meta-value">{{ currentEntrega.ejes_alfabeticos }}-{{ currentEntrega.ejes_alfabeticos_b }} / {{ currentEntrega.ejes_numericos }}—{{ currentEntrega.ejes_numericos_b }}</span>
                    </div>
                    <!-- ItemsAssociationInput moved to Supervisor column to keep associations with supervisor -->
                    <div class="meta-item mt-2">
                      <span class="meta-label">Especialidades</span>
                      <div>
                        <v-chip
                          v-for="(specialty, index) in currentEntrega.specialties"
                          :key="index"
                          color="#5E85EF"
                          dark
                          x-small
                          class="mr-1 mb-1">
                          {{ specialty.specialty.name }}
                        </v-chip>
                        <span class="meta-value" v-if="currentEntrega.specialties.length === 0">Sin especialidades</span>
                      </div>
                    </div>
                  </div>

                  <div class="supervisor-col" v-if="hasSupervisor">
                    <div class="meta-item">
                      <span class="meta-label">
                        <v-icon size="12" color="#2D4B95">mdi-account-circle</v-icon>
                        Supervisor
                      </span>
                      <span class="meta-value">
                        <div class="supervisor-card-compact">
                          <v-avatar size="30" class="mb-1">
                            <v-img v-if="supervisorDetails.basic_info?.avatar" :src="supervisorDetails.basic_info.avatar" alt="Supervisor"></v-img>
                            <v-icon v-else class="inner-user-avatar icon" color="#2D4B95" size="35">mdi-account-circle</v-icon>
                          </v-avatar>
                          <div class="supervisor-name-sm">{{ supervisorDetails ? supervisorDetails.name : 'Supervisor' }}</div>
                          <div class="supervisor-role-sm">Supervisor</div>
                          <v-btn
                            color="#3EBFA3"
                            dark
                            x-small
                            block
                            class="mt-2"
                            v-if="isSupervisor && !$store.entregaStore.state.currentEntrega.supervisor_approval && minApproversReached && 1 != 1"
                            :loading="loadingSupervisorApproval"
                            @click="approveSupervisor"
                          >
                            <v-icon left x-small>mdi-shield-check</v-icon>
                            Aprobar
                          </v-btn>
                        </div>
                      </span>
                    </div>

                    <div class="meta-item mt-2">
                      <ItemsAssociationInput :item="currentEntrega" item_type="entrega" @closeParent="minimizeItem" />
                    </div>
                  </div>
                </div>
              </section>

              <!-- ITO Countdown or Rejection Status -->
              <section class="content-section" v-if="(hasITOCountdown && currentEntrega.status < 3) || hasITORejection">
                <!-- ITO Rejection Alert -->
                <v-alert v-if="hasITORejection" color="red" border="left" outlined class="mb-0 countdown-alert">
                  <div class="d-flex align-center justify-space-between">
                    <div class="d-flex align-center">
                      <v-icon class="mr-3" size="16" color="red">mdi-close-circle</v-icon>
                      <div>
                        <div class="subtitle-1 font-weight-bold red--text">Rechazo ITO</div>
                        <div class="subtitle-2 font-weight-bold">El contador se ha pausado por rechazo ITO</div>
                        <div class="caption mt-1">Fecha de rechazo: {{ formatDateTime(currentEntrega.rechazoITODatetime) }}</div>
                      </div>
                    </div>
                    <v-chip color="red" text-color="white" small>
                      <v-icon left small>mdi-pause-circle</v-icon>
                      Pausado
                    </v-chip>
                  </div>
                </v-alert>

                <!-- Normal ITO Countdown -->
                <v-alert
                  v-else
                  :color="itoCountdownColor"
                  border="left"
                  outlined
                  class="mb-0 countdown-alert"
                  :class="{
                    'alert-pulse': itoCountdownData && itoCountdownData.isExpired,
                    'alert-outside-hours': !isWithinBusinessHours
                  }"
                >
                  <div class="d-flex align-center justify-space-between">
                    <div class="d-flex align-center">
                      <v-icon class="mr-3" size="16" :color="itoCountdownColor">{{ itoCountdownIcon }}</v-icon>
                      <div>
                        <div class="subtitle-1 font-weight-bold">
                          {{ itoCountdownData && itoCountdownData.isExpired ? 'Tiempo de revisión ITO vencido' : 'Tiempo restante para revisión ITO' }}
                        </div>
                        <div class="subtitle-2 font-weight-bold countdown-text" :class="itoCountdownColor + '--text'">
                          {{ itoCountdownText }}
                        </div>
                        <div class="caption mt-1">
                          Aprobación mínima alcanzada: {{ formatDateTime(currentEntrega.taskApprovedDateTime) }}
                        </div>
                        <div class="caption mt-1" v-if="currentConfig && currentConfig.horaInicio !== undefined">
                          <v-icon small class="mr-1">mdi-clock-outline</v-icon>
                          Horario laboral: {{ formatBusinessHours() }}
                          <span v-if="!isWithinBusinessHours" class="orange--text ml-2">(Fuera de horario)</span>
                        </div>
                      </div>
                    </div>
                    <div class="text-center">
                      <v-chip :color="isWithinBusinessHours ? 'green' : 'orange'" text-color="white" small class="mb-2">
                        <v-icon left small>{{ isWithinBusinessHours ? 'mdi-clock-check' : 'mdi-clock-alert' }}</v-icon>
                        {{ isWithinBusinessHours ? 'En horario' : 'Fuera de horario' }}
                      </v-chip>
                      <div v-if="!isWithinBusinessHours" class="caption text-center">
                        Próximo horario: {{ formatDateTime(nextBusinessDayStart) }}
                      </div>
                    </div>
                  </div>
                  <v-progress-linear
                    :value="itoReviewProgress"
                    :color="itoCountdownColor"
                    height="8"
                    rounded
                    class="mt-3"
                    :class="{ 'progress-paused': !isWithinBusinessHours }"
                  ></v-progress-linear>
                  <div class="d-flex justify-space-between align-center mt-2">
                    <div class="caption">
                      <v-icon small class="mr-1">mdi-clock-start</v-icon>
                      Inicio: {{ formatTime(itoCountdownData.approvedTime) }}
                    </div>
                    <div class="caption">
                      <v-icon small class="mr-1">mdi-clock-end</v-icon>
                      Límite: {{ formatTime(itoCountdownData.deadlineTime) }}
                    </div>
                  </div>
                  <v-alert
                    v-if="!isWithinBusinessHours && !itoCountdownData.isExpired"
                    color="amber"
                    border="left"
                    colored-border
                    dense
                    class="mt-2 mb-0"
                  >
                    <div class="d-flex align-center">
                      <v-icon color="amber" class="mr-2" small>mdi-information</v-icon>
                      <span class="caption">El contador se pausa fuera del horario laboral y fines de semana</span>
                    </div>
                  </v-alert>
                </v-alert>
              </section>

                <!-- Emplazamiento fila completa -->
                <div class="plano-full-row mt-3">
									<div class="d-flex align-center justify-space-between">
										<h3 class="section-title">
											Emplazamiento
										</h3>
                    <div class="d-flex align-center plano-actions">
                      <button
                        v-if="hasPlanoData"
                        class="upload-toggle-btn"
                        title="Ver todas las entregas de este plano"
                        @click="openPlanoTotalViewer"
                      >
                        <v-icon size="14" color="#2D4B95">mdi-map-search-outline</v-icon>
                        <span>Ver plano</span>
                      </button>
										<ControlPlanoModal
											ref="controlPlanoModal"
											:planos="planoArray"
											:plano-entrega="planoData"
											:small-btn="true"
											:no-trigger="!showTriggerControlPlano"
											@save-plano="editPlano"
										/>
                    </div>
									</div>
                  <div v-if="hasPlanoData" class="plano-viewer-container">
                    <img
                      v-if="!hasPlanoNodes && currentEntrega.plano_entrega?.screenshot?.url"
											:src="currentEntrega.plano_entrega.screenshot.url"
											style="height:100%; width:100%;object-fit:contain;"/>
                    <ForgePolygonViewer
                      v-else-if="currentPlanoIsForge"
                      :plano="currentPlanoObj"
                      :plano-data="planoData"
                      :read-only="true"
                      :forge-file-urn="currentPlanoForgeUrn"
                      :all-polygons="historicalPlanoPolygons"
                      :current-polygon-label="currentPlanoPolygonLabel"
                      :line-color="lineColor"
                      :fill-opacity="fillOpacity"
                      style="height:100%; width:100%;"
                    />
                    <ImagePolygonViewer
                      v-else
                      :plano="currentPlanoObj"
                      :plano-data="planoData"
                      :read-only="true"
                      :all-polygons="historicalPlanoPolygons"
                      :current-polygon-label="currentPlanoPolygonLabel"
                      :line-color="lineColor"
                      :fill-opacity="fillOpacity"
                      style="height:100%; width:100%;"
                    />
                  </div>
                  <div v-else class="description-body" v-html="currentEntrega.description"></div>
                </div>
              </section>

              <!-- Adjuntos + subida unificados -->
              <section class="content-section">
                <div class="section-header">
                  <h3 class="section-title mb-0">Adjuntos</h3>
                  <div class="d-flex" style="gap: 6px; align-items: center;">
                    <v-btn
                      v-if="hasMultipleAttachments"
                      rounded
                      color="#2D4B95"
                      dark
                      x-small
                      :loading="loadingFilePath"
                      @click="downloadAllAttachments"
                    >
                      <v-icon dark x-small>mdi-download-multiple</v-icon>
                      <span class="ml-1">Todo</span>
                    </v-btn>
                    <button
                      class="upload-toggle-btn"
                      :class="{ 'upload-toggle-btn--active': showUploadForm }"
                      @click="showUploadForm = !showUploadForm"
                    >
                      <v-icon size="14" :color="showUploadForm ? '#fff' : '#2D4B95'">mdi-upload</v-icon>
                      <span>{{ showUploadForm ? 'Cancelar' : 'Adjuntar' }}</span>
                    </button>
                  </div>
                </div>

                <AttachmentList
                  v-if="hasDownloadableAttachments"
                  :attachments="downloadableAttachments"
                  :loading="loadingFilePath"
                  @view="getFilesUrl"
                  @download="downloadAttachment"
                  @preview="previewAttachment" />
                <MediaGallery
                  v-if="hasMediaAttachments"
                  :items="mediaAttachments" />
                <p v-if="!hasDownloadableAttachments && !hasMediaAttachments && !showUploadForm" class="meta-value" style="color:#9CA3AF; margin-top: 6px;">Sin adjuntos</p>

                <!-- Adjuntos provenientes de aprobaciones de tareas -->
                <div v-if="hasApprovalAttachments" class="apv-attach-section">
                  <div class="apv-attach-divider">
                    <v-icon size="13" color="#6B7280">mdi-check-circle-outline</v-icon>
                    <span>Adjuntos de revisiones</span>
                  </div>

                  <div v-for="sub in approvalAttachmentsGrouped" :key="sub.subpartida" class="apv-subpartida">
                    <!-- Header subpartida -->
                    <div class="apv-subpartida-header">
                      <v-icon size="12" color="#2D4B95">mdi-folder-outline</v-icon>
                      <span class="apv-subpartida-label">{{ sub.subpartida }}</span>
                    </div>

                    <div v-for="task in sub.tasks" :key="task.id" class="apv-task">
                      <!-- Header tarea -->
                      <div class="apv-task-header">
                        <v-icon size="11" color="#9CA3AF">mdi-chevron-right</v-icon>
                        <span class="apv-task-label">{{ task.punto_control }}</span>
                      </div>

                      <!-- Grupos por jobtype -->
                      <div v-for="jt in task.jobtypes" :key="jt.jobtypeName" class="apv-jobtype">
                        <span class="pill pill--gray apv-jobtype-pill">
                          <v-icon size="10" color="#6B7280">mdi-account-outline</v-icon>
                          {{ jt.jobtypeName }}
                        </span>
                        <AttachmentList
                          :attachments="jt.attachments"
                          :loading="loadingFilePath"
                          @view="getFilesUrl"
                          @download="downloadAttachment"
                          @preview="previewAttachment"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Área de subida colapsable -->
                <div v-show="showUploadForm" class="upload-form-area">
                  <div class="upload-form-actions">
                    <button class="upload-trigger-btn" @click="onButtonClick">
                      <v-icon size="14" color="#2D4B95">mdi-file-plus</v-icon>
                      Seleccionar archivo
                    </button>
                    <input ref="uploader" multiple class="d-none" type="file" @change="onFilesChanged">
                    <v-btn
                      color="#5E85EF"
                      dark
                      small
                      :loading="loadingUpload"
                      :disabled="newFiles.length === 0"
                      @click="uploadAttachments"
                    >
                      <v-icon left small>mdi-cloud-upload</v-icon>
                      Subir
                    </v-btn>
                  </div>
                  <div v-if="newFiles.length > 0" class="mt-2">
                    <div v-for="(file, index) in newFiles" :key="index" class="new-file-row">
                      <v-icon size="14" color="#2D4B95" class="mr-2">mdi-file-document-outline</v-icon>
                      <span class="new-file-name">{{ file.name }}</span>
                      <button class="remove-file-btn" @click="removeNewAttachment(index)">
                        <v-icon size="13" color="#F4516C">mdi-close</v-icon>
                      </button>
                    </div>
                  </div>
                  <div v-else class="upload-empty-state">
                    <v-icon size="28" color="#D1D5DB">mdi-file-upload-outline</v-icon>
                    <p class="upload-empty-text">Selecciona un archivo para adjuntar</p>
                  </div>
                </div>
              </section>

              <!-- Closure Signature -->
              <ClosureSignature
                v-if="currentEntrega.status == 3 && closureSignatureData && closureSignatureData.approval_checks.length > 0"
                :item="closureSignatureData"
                item-type="entrega"
                :item-label="currentEntrega.title"
              />

            </div>
          </div>

          <!-- RESIZER -->
          <div
            v-if="!$vuetify.breakpoint.mobile"
            class="panel-resizer"
            @mousedown.prevent="startResize"
          >
            <div class="resizer-handle"></div>
          </div>

          <!-- RIGHT: approvals panel -->
          <div class="panel-approvals" :style="$vuetify.breakpoint.mobile ? {} : { width: (100 - leftPanelWidth) + '%' }">
            <!-- Header fijo fuera del scroll -->
            <div class="approvals-header">
              <!-- Main header row: title + two donuts + buttons -->
              <div class="approvals-header-main">
                <h3 class="approvals-title">Aprobación de Tareas</h3>
                
                <!-- Donuts row -->
                <div class="donuts-row">
                  <!-- Donut 1: Tareas aprobadas -->
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <div class="donut-item" v-bind="attrs" v-on="on">
                        <div class="donut-container">
                          <v-progress-circular
                            :value="totalTasks > 0 ? (approvedTasksCount / totalTasks) * 100 : 0"
                            color="#3EBFA3"
                            size="45"
                            width="5"
                          >
                            <span class="donut-text" style="font-size: 11px;">{{ approvedTasksCount }}/{{ totalTasks }}</span>
                          </v-progress-circular>
                        </div>
                        <span class="donut-label">Aprobadas</span>
                      </div>
                    </template>
                    <span>{{ approvedTasksCount }} de {{ totalTasks }} tareas aprobadas</span>
                  </v-tooltip>
                  
                  <!-- Donut 2: Aprobaciones requeridas -->
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <div class="donut-item" v-bind="attrs" v-on="on">
                        <div class="donut-container">
                          <v-progress-circular
                            :value="minApprovers > 0 ? (allApproved / minApprovers) * 100 : 0"
                            color="#5E85EF"
                            size="30"
                            width="4"
                          >
                            <span class="donut-text">{{ allApproved }}/{{ minApprovers }}</span>
                          </v-progress-circular>
                        </div>
                        <span class="donut-label">Requeridos</span>
                      </div>
                    </template>
                    <span>{{ allApproved }} de {{ minApprovers }} aprobadores requeridos</span>
                  </v-tooltip>
                </div>
                
                <!-- Action buttons -->
                <div class="approvals-header-actions">
                  <v-btn
                    color="#3EBFA3"
                    dark
                    x-small
                    v-if="isITO && !allTasksAutocontrol && hasITOCountdown"
                    :loading="loadingAutocontrolTotal"
                    @click="enableAutocontrolTotal"
                  >
                    <v-icon left x-small>mdi-checkbox-marked-circle-outline</v-icon>
                    Autocontrol
                  </v-btn>
                  <v-btn
                    color="#2D4B95"
                    dark
                    x-small
                    v-if="isSupervisor && currentEntrega.status == 3"
                    :loading="loadingFinalApproval"
                    @click="showPasswordModal"
                  >
                    <v-icon left x-small>mdi-check-decagram</v-icon>
                    Firmar
                  </v-btn>
                </div>
              </div>
            </div>

            <div class="panel-scroll">
              <div class="tasks-list">
								<div class="d-flex justify-end">
                  <v-btn
                    color="#2D4B95"
                    dark
                    x-small
                    v-if="currentEntrega.tasks && currentEntrega.tasks.length > 0 && currentEntrega.status < 3 && checkPermissions(['interact_entregas'])"
                    @click="showBulkApprovalModal = true"
                  >
                    <v-icon left x-small>mdi-playlist-check</v-icon>
                    Revisión simultánea de tareas
                  </v-btn>
								</div>
                <TaskRow
                  v-for="item in currentEntrega.tasks"
                  :key="item.id"
                  :task="item"
                  :jobTypes="filteredJobTypes"
                  :isITO="isITO"
									:entrega="currentEntrega"
                  :hasGlobalITORejection="hasITORejection"
                  :meetsMinimum="minApproversReached"
                  :autocontrol="item.autocontrol"
                  :loadingAutocontrol="loadingAutocontrol === item.id"
									:config="currentConfig"
                  @approval="setAprobacion"
                  @history="showHistory"
                  @autocontrol="enableAutocontrol"
                />
                <div v-if="!currentEntrega.tasks || currentEntrega.tasks.length === 0" class="tasks-empty">
                  <v-icon size="40" color="#D1D5DB">mdi-clipboard-list-outline</v-icon>
                  <p class="tasks-empty-text">No hay tareas para esta entrega</p>
                </div>
              </div>
            </div>
          </div>

        </div><!-- end modal-body -->
      </v-card>
    </v-dialog>
    
    <!-- Task Detail Modal -->
    <v-dialog
      v-model="taskDetailDialog"
      max-width="600"
      scrollable
    >
      <v-card v-if="selectedTask">
        <v-card-title class="text-primary">
          Detalles de Tarea
          <v-spacer></v-spacer>
          <v-btn icon @click="taskDetailDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <h3 class="subtitle-1 font-weight-bold">{{ selectedTask.punto_control }}</h3>
              <p class="caption text--secondary">{{ selectedTask.subpartida }}</p>
              
              <v-divider class="my-3"></v-divider>
              
              <v-list-item two-line>
                <v-list-item-content>
                  <v-list-item-title>Estado</v-list-item-title>
                  <v-list-item-subtitle>
                    <v-chip
                      :color="getTaskApprovalColor(selectedTask)"
                      text-color="white"
                      small
                      class="mt-1"
                    >
                      {{ getTaskApprovalText(selectedTask) }}
                    </v-chip>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
              
              <v-list-item two-line v-if="isTaskApproved(selectedTask)">
                <v-list-item-content>
                  <v-list-item-title>Aprobado por</v-list-item-title>
                  <v-list-item-subtitle class="d-flex align-center mt-1">
                    <v-avatar size="24" class="mr-2">
                      <v-img :src="getApproverAvatar(selectedTask)" alt="Approver"></v-img>
                    </v-avatar>
                    {{ getApproverName(selectedTask) }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
              
              <v-list-item two-line v-if="selectedTask.autocontrol">
                <v-list-item-content>
                  <v-list-item-title>Autocontrol</v-list-item-title>
                  <v-list-item-subtitle>
                    <v-chip
                      color="#fb8c00"
                      text-color="white"
                      small
                      class="mt-1"
                    >
                      Habilitado
                    </v-chip>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
              
              <v-textarea
                v-model="taskComment"
                label="Comentario"
                outlined
                rows="3"
                hide-details
                class="mt-3"
              ></v-textarea>
            </v-col>
          </v-row>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          
          <!-- Autocontrol button (only for ITO) -->
          <v-btn
            color="#fb8c00"
            text
            v-if="isITO && !selectedTask.autocontrol"
            :loading="loadingAutocontrol === selectedTask.id"
            @click="enableAutocontrol(selectedTask)"
          >
            <v-icon left>mdi-shield-account</v-icon>
            Permitir Autocontrol
          </v-btn>
          
          <!-- Task approval button (only for ITO or if task has autocontrol enabled) -->
          <v-btn
            color="#3EBFA3"
            dark
            v-if="(isITO || selectedTask.autocontrol) && !isTaskApproved(selectedTask)"
            :loading="loadingApproval === selectedTask.id"
            @click="approveTask(selectedTask, taskComment)"
          >
            <v-icon left>mdi-check-circle</v-icon>
            Firmar Tarea
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Modals -->
    <AcceptCancelModal 
      :acceptRejectModal="acceptRejectModal"
      :acceptRejectModalData="acceptRejectModalData"
      :closeModal="closeModalAcceptCancel" />
    
    <SendContentByEmailModal 
      :sendContentByEmailModal="sendByEmailModal" 
      :closeModal="closeSendEmailModal"
      :sendingType="2" />
    
    <AprobacionMasivaModal
      ref="bulkModal"
      :show="showBulkApprovalModal"
      :tasks="currentEntrega.tasks || []"
      :entrega-id="currentEntrega.id"
      :user-subject="userCurrentSubject"
      :is-i-t-o="isITO"
      @saved="savedTask"
      @close="closeBulkApprovalModal"
    />

    <AprobacionModal
      ref="aprobacionModal"
      :aprobacion="aprobacion" 
      @savedTask="savedTask" 
      @closeModal="closeAprobacionModal" />
      
    <ApprovalHistoryModal
      :show="historyModal.show"
      :taskId="historyModal.taskId"
      @close="closeHistoryModal"
    />
    
    <PasswordConfirmationModal
      ref="passwordModal"
      :show.sync="passwordModal"
      @confirm="handlePasswordConfirm"
      @cancel="passwordModal = false"
    />

    <GlobalFileViewer
      :media-items="viewerMediaItems"
      :media-index="viewerMediaIndex"
      :document-item="viewerDocumentItem"
      :forge-item="viewerForgeItem"
      @close="closeAttachmentViewer"
      @minimize-file="minimizeAttachmentViewer"
      @error="onViewerError"
      @load="onViewerLoad"
    />
  </div>
</template>

<script>
import {
  Heading, Bold, Italic, Strike, Underline, Code, Paragraph, 
  BulletList, OrderedList, ListItem, Link, Blockquote, 
  HardBreak, HorizontalRule, History
} from 'tiptap-vuetify'

import { v4 as uuidv4 } from 'uuid'
import EventBus from '@/helpers/event_bus'
import { getPublicUrl, getTareaByID } from '@/helpers/api/tasks'
import { getRdiByID, deleteRdi } from '@/helpers/api/rdi'
import { getZippedFiles } from '@/helpers/api/fileUploads'
import { getEntregaByID, approveSupervisor, addInteraction, closeEntrega, setTaskApproval, editPlanoEntrega } from '@/helpers/api/entrega'
import { verifyUserPassword } from '@/helpers/api/user'
import { calculateBusinessHoursDeadline, calculateRemainingBusinessTime, isWithinWorkingHours, nextWorkingPeriodStart, } from '@/helpers/workingHoursHelper'

// Components
import MediaGallery from '@/components/Globals/Helpers/MediaGallery'
import GlobalFileViewer from '@/components/Globals/Helpers/GlobalFileViewer'
import { normalizeAttachmentForPreview, toMediaViewerItem } from '@/helpers/attachmentPreview'
import SendContentByEmailModal from '@/components/Globals/Modals/General/SendContentByEmailModal'
import AcceptCancelModal from '@/components/Globals/Modals/General/AcceptCancelModal.vue'
import AprobacionModal from './AprobacionModal.vue'
import AprobacionMasivaModal from './AprobacionMasivaModal.vue'
import ControlPlanoModal from './ControlPlanoModal.vue'
import ForgePolygonViewer from './ForgePolygonViewer.vue'
import ImagePolygonViewer from './ImagePolygonViewer.vue'
import TaskRow from './Parts/TaskRow.vue'
import AttachmentList from './Parts/AttachmentList.vue'
import ApprovalHistoryModal from './ApprovalHistoryModal.vue'
import PasswordConfirmationModal from './PasswordConfirmationModal.vue'
import ItemsAssociationInput from '@/components/Globals/Helpers/ItemsAssociationInput'
import { downloadPDF, deleteEntrega } from '@/helpers/api/entrega'
import ClosureSignature from '@/components/Globals/Helpers/ClosureSignature'
import HeaderButtons from './Parts/HeaderButtons.vue'
import BotSummarizeMini from '@/components/Globals/Helpers/BotSummarizeMini'
import SeenByUsers from '@/components/Globals/Helpers/SeenByUsers'
import ModalStickyHeader from '@/components/Globals/Helpers/ModalStickyHeader'

function formatPolygonLabel (item) {
  const number = item && item.entrega ? item.entrega.number : item && item.number
  return number != null ? `Nº${number}` : 'Entrega'
}

export default {
  name: 'EntregaModal',
  
  components: { 
    ModalStickyHeader,
    MediaGallery, 
    GlobalFileViewer,
    SendContentByEmailModal, 
    AcceptCancelModal, 
    HeaderButtons,
    AprobacionModal,
    AprobacionMasivaModal,
    TaskRow,
    AttachmentList,
    ControlPlanoModal,
    ForgePolygonViewer,
    ImagePolygonViewer,
    ApprovalHistoryModal,
    PasswordConfirmationModal,
		ItemsAssociationInput,
		ClosureSignature,
		BotSummarizeMini,
                SeenByUsers
  },
  
  data() {
    return {
      seenByMenu: false,
      aprobacion: null,
      showMore: false,
      interactionSearch: '',
      extensions: [
        History,
        Blockquote,
        Link,
        Underline,
        Strike,
        Italic,
        ListItem,
        BulletList,
        OrderedList,
        [Heading, {
          options: {
            levels: [1, 2, 3]
          }
        }],
        Bold,
        Code,
        HorizontalRule,
        Paragraph,
        HardBreak
      ],
      message: '',
      response_type: 0,
      need_reload: false,
      files: [],
      filesPreview: [],
      newFiles: [],
      showUploadForm: false,
      loadingFilePath: false,
      is_response: false,
      loading: false,
      changeInChecklists: false,
      loadingChangeUser: false,
      loadingPDFDownload: false,
      loadingApproval: null,
      loadingAutocontrol: null,
      loadingAutocontrolTotal: false,
      loadingSupervisorApproval: false,
      loadingFinalApproval: false,
      loadingUpload: false,
      viewerMediaItems: [],
      viewerMediaIndex: null,
      viewerDocumentItem: null,
      viewerForgeItem: null,
      sendByEmailModal: false,
      cancelTimer: 4,
      inCancelTime: false,
      cancelInterval: null,
      confirmButtonText: 'Responder',
      loadingEdit: false,
      acceptRejectModal: false,
      acceptRejectModalData: null,
      historyModal: {
        show: false,
        taskId: null
      },
      passwordModal: false,
      verifyingPassword: false,
      taskDetailDialog: false,
      selectedTask: null,
      taskComment: '',
      currentTime: new Date(),
      timeUpdateInterval: null,
      leftPanelWidth: 55,
      isResizing: false,
      showBulkApprovalModal: false
      ,lineColor: '#2D4B95',
      fillOpacity: 0.3
    };
  },
  
  computed: {
    currentEntrega() {
      return this.$store.entregaStore.state.currentEntrega || {};
    },
		isClosed(){
			return this.currentEntrega.status === 3 || this.currentEntrega.status === 4;
		},
		isCreator(){
			return this.currentEntrega.created_by === this.$store.userStore.state.userInfo.id;
		},
    planoArray(){
      return [this.currentEntrega.plano]
    },
    zonaName(){
      const zona = this.zonasList.find(z => z.id === this.currentEntrega.zona_id)
      return zona ? zona.name : ''
    },
    currentConfig() {
      return this.$store.entregaStore.state.config;
    },
    showTriggerControlPlano() {
			return this.checkPermissions(['edit_site_entregas']) || this.isCreator;
		},
    zonasList() {
      const config = this.$store.entregaStore.state.config || {}
      return (config.zonas || []).map(zona => ({
        id: zona.id,
        name: zona.name,
        niveles: zona.niveles || [],
        ejesAlfabeticos: zona.ejesAlfabeticos || [],
        ejesNumericos: zona.ejesNumericos || []
      }))
    },
    currentJobTypes() {
      return this.$store.usersStore.state.jobTypes || [];
    },
    
    filteredJobTypes() {
			return this.currentJobTypes.filter(jobType =>
				this.currentConfig.aprobadores?.some(
					aprobador => Number(aprobador.id) === Number(jobType.id)
				)
			);
		},
    
    hasMediaAttachments() {
      return this.currentEntrega.attachments && 
             this.currentEntrega.attachments.filter(item => item.pathmedia).length > 0;
    },
    
    mediaAttachments() {
      if (!this.hasMediaAttachments) return [];
      return this.currentEntrega.attachments
        .filter(item => item.pathmedia)
        .map(item => item.pathmedia);
    },
    
    hasMultipleAttachments() {
      return this.currentEntrega.attachments && this.currentEntrega.attachments.length > 1;
    },
    
    hasDownloadableAttachments() {
      return this.currentEntrega.attachments && 
             this.currentEntrega.attachments.filter(item => !item.pathmedia).length > 0;
    },
    
    downloadableAttachments() {
      if (!this.hasDownloadableAttachments) return [];
      return this.currentEntrega.attachments.filter(item => !item.pathmedia);
    },
    
    // Adjuntos provenientes de approvals de tareas
    hasApprovalAttachments() {
      // Return true if there is at least one approval attachment in tasks
      return (this.currentEntrega.tasks || []).some(task =>
        (task.approvals || []).some(a => a.attachment && a.attachment.file)
      );
    },

    approvalAttachmentsFlat() {
      if (!this.currentEntrega || !this.currentEntrega.tasks) return [];
      const list = [];
      this.currentEntrega.tasks.forEach(task => {
        const taskId = task.id;
        const taskName = task.punto_control;
        const subpartida = task.subpartida || 'Sin Partida';
        (task.approvals || []).forEach(approval => {
          const file = approval.attachment && approval.attachment.file ? approval.attachment.file : null;
          if (!file) return;
          list.push({
            file,
            taskId,
            taskName,
            subpartida,
            approvalId: approval.id,
            jobtypeName: approval.jobtype?.name || (approval.user?.name) || ('Subject ' + (approval.subject_id || '')),
            user: approval.user || null,
            created_at: approval.created_at || approval.updated_at || null
          });
        });
      });
      // Sort by subpartida, then taskName, then jobtypeName
      return list.sort((a, b) => {
        if (a.subpartida === b.subpartida) {
          if (a.taskName === b.taskName) {
            return (a.jobtypeName || '').localeCompare(b.jobtypeName || '');
          }
          return (a.taskName || '').localeCompare(b.taskName || '');
        }
        return (a.subpartida || '').localeCompare(b.subpartida || '');
      });
    },

    approvalAttachmentsGrouped() {
      const flat = this.approvalAttachmentsFlat || [];
      const groups = {};
      flat.forEach(item => {
        const sp = item.subpartida || 'Sin Partida';
        if (!groups[sp]) groups[sp] = { subpartida: sp, tasks: {} };
        const tasksMap = groups[sp].tasks;
        if (!tasksMap[item.taskId]) {
          tasksMap[item.taskId] = { id: item.taskId, punto_control: item.taskName, jobtypes: {} };
        }
        const jtName = item.jobtypeName || 'Otro';
        if (!tasksMap[item.taskId].jobtypes[jtName]) {
          tasksMap[item.taskId].jobtypes[jtName] = { jobtypeName: jtName, attachments: [] };
        }
        // push an object shaped like currentEntrega.attachments items: { file: ... }
        tasksMap[item.taskId].jobtypes[jtName].attachments.push({ file: item.file, meta: { approvalId: item.approvalId, created_at: item.created_at, user: item.user } });
      });
      // Convert to arrays and sort
      const result = Object.values(groups).map(g => {
        const tasks = Object.values(g.tasks).map(t => {
          const jobtypes = Object.values(t.jobtypes).map(jt => ({
            jobtypeName: jt.jobtypeName,
            attachments: jt.attachments
          })).sort((x, y) => (x.jobtypeName || '').localeCompare(y.jobtypeName || ''));
          jobtypes.sort((a, b) => (a.jobtypeName || '').localeCompare(b.jobtypeName || ''));
          return { id: t.id, punto_control: t.punto_control, jobtypes };
        }).sort((a, b) => (a.punto_control || '').localeCompare(b.punto_control || ''));
        return { subpartida: g.subpartida, tasks };
      }).sort((a, b) => (a.subpartida || '').localeCompare(b.subpartida || ''));
      return result;
    },
    
    hasPlanoData() {
      return this.currentEntrega && 
        this.currentEntrega.plano_id && 
        this.currentEntrega.plano;
    },

    hasPlanoNodes() {
      return !!(this.currentEntrega && this.currentEntrega.plano_entrega &&
        this.currentEntrega.plano_entrega.nodes && this.currentEntrega.plano_entrega.nodes.length > 0);
    },

    currentPlanoPolygonLabel() {
      return formatPolygonLabel(this.currentEntrega);
    },

    historicalPlanoPolygons() {
      const currentId = this.currentEntrega.plano_entrega && this.currentEntrega.plano_entrega.id;
      return (this.currentEntrega.plano && this.currentEntrega.plano.plano_entregas || [])
        .filter(item => item.id !== currentId && item.nodes && item.nodes.length > 0)
        .map(item => ({
          id: item.id,
          nodes: item.nodes,
          color: item.color || '#2D4B95',
          opacity: item.opacity != null ? item.opacity : 0.3,
          label: formatPolygonLabel(item)
        }));
    },
    
    planos() {
      return this.$store.entregaStore.state.activePlanos || [];
    },
    
    planoData() {
      if (!this.currentEntrega || !this.currentEntrega.plano_entrega) {
        return null;
      }
      return {
      seenByMenu: false,
				...this.currentEntrega.plano_entrega,
				plano_entregas: [this.currentEntrega.plano_entrega] ?? []
			};
    },

    // Helpers para visor de plano
    currentPlanoObj() {
      return this.planoArray && this.planoArray.length ? this.planoArray[0] : null;
    },
    currentPlanoIsForge() {
      const p = this.currentPlanoObj;
      return !!(p && (p.file?.forge_file?.urn || p.forge_file?.urn));
    },
    currentPlanoForgeUrn() {
      const p = this.currentPlanoObj;
      return p?.file?.forge_file?.urn || p?.forge_file?.urn || null;
    },
    
    isITO() {
      return this.$store.projectsStore.state.selectedProject && 
        this.$store.projectsStore.state.selectedProject.job_type && 
        this.$store.projectsStore.state.selectedProject.job_type.id === 4;
    },

    userCurrentSubject() {
      const selectedProject = this.$store.projectsStore.state.selectedProject;
      if (!selectedProject || !selectedProject.job_type) return null;
      return this.filteredJobTypes.find(j => j.id === selectedProject.job_type.id) || null;
    },
    
    isSupervisor() {
      return this.currentEntrega.supervisor_id == this.$store.userStore.state.userInfo.id;
    },
    
    hasSupervisor() {
      return !! this.currentEntrega.supervisor_id;
    },
    
    supervisorDetails() {
			return this.currentEntrega?.supervisor
      // const config = this.$store.entregaStore.state.config || {};
      // if (!config.supervisor) return null;

      // const supervisor = this.$store.usersStore.state.usersList.find(
      //   user => user.id === this.currentEntrega.supervisor_id
      // );
      // return supervisor || null;
    },
    
    totalTasks() {
      return this.currentEntrega?.tasks?.length || 0;
    },
    
    allApproved(){
      if (!this.currentEntrega?.tasks) return 0;
      if(!this.currentConfig || !this.currentConfig.aprobadores) return 0;
      
      // Para cada aprobador, contar cuántas tareas tiene aprobadas (usando latest approval)
      let approversWithAllApproved = 0;
      
      this.currentConfig.aprobadores.forEach(aprobador => {
				const subjectId = aprobador.id
        let approvedTasksForThisApprover = 0;
        
        this.currentEntrega.tasks.forEach(task => {
          if (!task.approvals || task.approvals.length === 0) return;
          
          // Obtener el approval más reciente para este aprobador en esta tarea
          const approvalsBySubject = task.approvals.filter(a => a.subject_id === subjectId);
          if (approvalsBySubject.length === 0) return;
          
          const latestApproval = approvalsBySubject.reduce((latest, current) => 
            current.id > latest.id ? current : latest
          );
          
          if (latestApproval.estado === 'Aprobado') {
            approvedTasksForThisApprover++;
          }
        });
        
        // Si este aprobador aprobó todas las tareas, incrementar el contador
        if (approvedTasksForThisApprover === this.currentEntrega.tasks.length) {
          approversWithAllApproved++;
        }
      });
      
      return approversWithAllApproved;
    },
    
    approvedTasksCount() {
      if (!this.currentEntrega?.tasks) return 0;
      return this.currentEntrega.tasks.filter(task => this.isTaskApproved(task)).length;
    },
    approvedTasksITO() {
      if (!this.currentEntrega?.tasks) return 0;
      return this.currentEntrega.tasks.filter(task => this.isTaskItoApproved(task)).length;
    },
    approvedTasksAutocontrol() {
      if (!this.currentEntrega?.tasks) return 0;  
      return this.currentEntrega.tasks.filter(task => this.isTaskAutocontrolApproved(task)).length;
    },
    minApprovers() {
      return this.currentConfig.minimo || 0;
    },
    
    totalApprovers() {
      return this.currentConfig.aprobadores?.length || 0;
    },
    
    currentApproversCount() {
      return Math.min(this.approvedTasksCount, this.totalApprovers);
    },
    
    allTasksAutocontrol() {
      if (!this.currentEntrega?.tasks?.length) return false;
      return this.currentEntrega.tasks.every(task => task.autocontrol);
    },
    
    isSupervisorApproved(){
      return this.currentEntrega?.status == 3;
    },

    closureSignatureData() {
      const entrega = this.currentEntrega
      if (!entrega || !entrega.tasks) return null

      // Recopilar todas las aprobaciones de todas las tareas
      const allApprovals = entrega.tasks.flatMap(task => task.approvals || [])

      // Filtrar únicos por user_id (mantener el más reciente)
      const seen = new Set()
      const uniqueApprovals = allApprovals
        .filter(a => a.estado === 'Aprobado')
        .filter(a => {
          if (seen.has(a.user_id)) return false
          seen.add(a.user_id)
          return true
        })
        .map(a => ({
          user: a.user,
          jobtype: a.jobtype,
          created_at: a.created_at
        }))
				
      return {
      seenByMenu: false,
        project: entrega.project,
        approval_checks: uniqueApprovals,
        approved_status_change: entrega.completedDateTime
          ? { created_at: entrega.completedDateTime }
          : entrega.taskApprovedDateTime
            ? { created_at: entrega.taskApprovedDateTime }
            : null,
        verification_code: entrega.verification_code || null
      }
    },
    
    minApproversReached(){
      return this.allApproved >= this.minApprovers
    },
    
    // Enhanced ITO Countdown Computed Properties
    hasITOCountdown() {
      return this.currentEntrega?.taskApprovedDateTime && 
             this.currentConfig?.tiempoMaximoITO && 
             this.minApproversReached;
    },
    
    // ITO Rejection Status
    hasITORejection() {
      return this.currentEntrega?.rechazoITODatetime;
    },

   
		itoCountdownData() {
			if (!this.hasITOCountdown) return null;

			const approvedTime = new Date(this.currentEntrega.taskApprovedDateTime);
			const maxHours     = this.currentConfig.tiempoMaximoITO;
			const schedule     = this.currentConfig.work_schedule || [];

			const deadlineTime = calculateBusinessHoursDeadline(approvedTime, maxHours, schedule).toDate();
			const remainingMs  = calculateRemainingBusinessTime(this.currentTime, deadlineTime, schedule);

			return {
      seenByMenu: false,
				approvedTime,
				deadlineTime,
				remainingMs,
				isExpired: remainingMs <= 0,
				totalHours: maxHours,
			};
		},
  
    itoCountdownText() {
      if (!this.itoCountdownData) return '';
      
      const { remainingMs, isExpired } = this.itoCountdownData;
      
      if (isExpired) return 'TIEMPO VENCIDO';

      // Check if we're outside business hours
      if (!this.isWithinBusinessHours) {
        return `FUERA DE HORARIO - ${this.formatTimeRemaining(remainingMs)}`;
      }

      return this.formatTimeRemaining(remainingMs);
    },

    itoCountdownColor() {
      if (!this.itoCountdownData) return 'grey';
      
      const { remainingMs, totalHours } = this.itoCountdownData;
      const totalMs = totalHours * 60 * 60 * 1000;
      const percentRemaining = (remainingMs / totalMs) * 100;
      
      if (remainingMs <= 0) return 'red';
      if (percentRemaining <= 25) return 'red';
      if (percentRemaining <= 50) return 'orange';
      if (percentRemaining <= 75) return 'amber';
      return 'green';
    },

    itoCountdownIcon() {
      if (!this.itoCountdownData) return 'mdi-timer-outline';
      
      const { isExpired } = this.itoCountdownData;
      const isBusinessHours = this.isWithinBusinessHours;
      
      if (isExpired) return 'mdi-timer-off-outline';
      if (!isBusinessHours) return 'mdi-clock-time-eight-outline';
      return 'mdi-timer-outline';
    },

    itoReviewProgress() {
      if (!this.itoCountdownData) return 0;
      
      const { remainingMs, totalHours } = this.itoCountdownData;
      const totalMs = totalHours * 60 * 60 * 1000;
      const elapsed = totalMs - remainingMs;
      
      return Math.min(100, Math.max(0, (elapsed / totalMs) * 100));
    },

		isWithinBusinessHours() {
			const schedule = this.currentConfig?.work_schedule || [];
			return isWithinWorkingHours(this.currentTime, schedule);
		},
    
		nextBusinessDayStart() {
			const schedule = this.currentConfig?.work_schedule || [];
			// nextWorkingPeriodStart skips the current window and finds the next one
			return nextWorkingPeriodStart(this.currentTime, schedule).toDate();
		},
    
    canSignFinal() {
      const minApproversReached = this.minApproversReached
      const supervisorApproved = this.currentEntrega?.autocontrol || this.approvedTasksITO + this.approvedTasksAutocontrol > this.totalTasks ;
      return minApproversReached && supervisorApproved;
    }
  },
  
  watch: {
    currentEntrega(newData) {
      if (newData && !newData.loading) {
        const planoId = newData.plano?.id || newData.plano_id
        if (planoId) this.loadPlanoData(planoId)
        this.$nextTick(() => { this.routeDraftIfNeeded() });
      }
    }
  },
  
  mounted() {
    EventBus.$on("entregaModalOpened", this.onEntregaModalOpened);
    this.updateNow();
    setInterval(this.updateNow.bind(this), 1000);
    window.addEventListener('mousemove', this.doResize);
    window.addEventListener('mouseup', this.stopResize);
    this.fileListeners()
  },
  
  beforeDestroy() {
    EventBus.$off("entregaModalOpened", this.onEntregaModalOpened);
    window.removeEventListener('mousemove', this.doResize);
    window.removeEventListener('mouseup', this.stopResize);
  },
  
  methods: {
    openPlanoTotalViewer() {
      if (!this.currentEntrega) return
      const planoId = this.currentEntrega.plano?.id || this.currentEntrega.plano_id
      if (!planoId) return
      this.$store.entregaStore.commit('setPlanoTotalViewerPlanoId', planoId)
      this.$store.entregaStore.commit('setPlanoTotalViewerModal', true)
    },

		async getEntregaInfo(){
			const entregaData = await getEntregaByID({ entrega_id: this.currentEntrega.id });
      if (entregaData.code === 403) {
        this.$store.entregaStore.commit('setCurrentEntrega', null);
      } else if (entregaData.code === 200) {
				this.$store.entregaStore.commit('setCurrentEntrega', entregaData.entrega);
			}
		},

    onEntregaModalOpened() {
      // When modal opens, attempt to route any pending draft
      try { this.$nextTick(() => { this.routeDraftIfNeeded() }) } catch (e) { console.warn('onEntregaModalOpened error', e) }
    },

    // Centralized draft routing: call this after currentEntrega is loaded
    routeDraftIfNeeded() {
      const draft = this.$store.entregaStore.state.draftToLoad
      if (!draft) return

      // normalize draft data (may be JSON string)
      let data = draft.data
      try { if (typeof data === 'string') data = JSON.parse(data) } catch (e) { console.warn('routeDraftIfNeeded: parse draft.data failed', e) }

      // determine entrega id from draft payload (accept both snake and camel)
      const entregaId = data?.entregaId ?? data?.entrega_id
      if (!entregaId || Number(entregaId) !== Number(this.currentEntrega.id)) return

      const raw = Number(draft.raw_type ?? draft.type ?? 0)

      if (raw === 11) {
        // individual approval: populate aprobacion and open modal
        const approvalData = data || {}
        this.setAprobacion({
          subjectId: approvalData.subjectId ?? approvalData.subject_id ?? null,
          subject: approvalData.subject || null,
          taskId: approvalData.taskId ?? approvalData.task_id ?? null,
          taskName: approvalData.taskName ?? approvalData.task_name ?? null,
          estado: approvalData.estado ?? 'Aprobado',
          comentario: approvalData.comentario ?? '' ,
          attachment: null
        })

        // Wait for child to react to prop change, then call its loadDraft
        this.$nextTick(() => {
          try {
            const ref = this.$refs.aprobacionModal
            if (ref && typeof ref.loadDraft === 'function') ref.loadDraft(draft)
          } catch (e) { console.warn('aprobacionModal.loadDraft failed', e) }
        })
      } else if (raw === 12) {
        // bulk approval: open bulk modal and load draft
        this.showBulkApprovalModal = true
        this.$nextTick(() => {
          try {
            const ref = this.$refs.bulkModal
            if (ref && typeof ref.loadDraft === 'function') ref.loadDraft(draft)
          } catch (e) { console.warn('bulkModal.loadDraft failed', e) }
        })
      }

      // Clear routed draft so child modals don't re-process it
      this.$store.entregaStore.commit('setDraftToLoad', null)
    },
    fileListeners() {
      EventBus.$on('completedFileUpload', (payload) => {
        if (payload && payload.entrega_id === this.currentEntrega.id) {
					this.getEntregaInfo();
        }
      })
    },
		startDeleteEntrega() {
			this.acceptRejectModalData = {
				title: '¿Eliminar entrega?',
				content_a: '¿Estás seguro de que deseas eliminar esta entrega? Esta acción no se puede deshacer.',
				type: 3
			};
			this.acceptRejectModal = true;
		},
		async deleteCurrentEntrega() {
			await deleteEntrega({ entrega_id: this.currentEntrega.id })

			this.need_reload = true
			this.closeModal()
		},
		closeBulkApprovalModal(reload = false) {
			this.showBulkApprovalModal = false;
			if(reload) {
				this.getEntregaInfo();
			}
		},
		async editPlano(plano_data) {
			if(!this.currentEntrega.plano_entrega) {
				return;
			}
			if(plano_data.screenshot){
				this.onFileChanged(plano_data.screenshot, 17)
			}
			await editPlanoEntrega({
				plano_entrega_id: this.currentEntrega.plano_entrega?.id,
				...plano_data
			})
			this.getEntregaInfo();
		},

    openPlanoModal() {
      const c = this.$refs.controlPlanoModal;
      if (c && typeof c.openModal === 'function') {
        try { c.openModal(); } catch (e) { console.warn('openPlanoModal:', e) }
      }
    },
    startResize() {
      this.isResizing = true;
    },

    doResize(e) {
      if (!this.isResizing) return;
      const dialog = this.$refs.modalBody;
      if (!dialog) return;
      const rect = dialog.getBoundingClientRect();
      const newWidth = ((e.clientX - rect.left) / rect.width) * 100;
      if (newWidth > 25 && newWidth < 75) {
        this.leftPanelWidth = newWidth;
      }
    },

    stopResize() {
      this.isResizing = false;
    },

    /**
     * Calculate deadline time considering business hours
     */
    downloadEntregaPdf() {
      downloadPDF({ entrega_id: this.currentEntrega.id })
        .then(response => {
          if (response && response.code === 200 && response.pdf_url) {
            // Create a link element to download from the S3 URL
            const link = document.createElement('a');
            link.href = response.pdf_url;
            link.setAttribute('download', response.filename || `entrega_${this.currentEntrega.number}.pdf`);
            link.setAttribute('target', '_blank'); // Open in new tab as fallback
            document.body.appendChild(link);
            link.click();
            link.remove();
          } else {
            console.error('Invalid response format:', response);
            this.$store.commit('showSnackbar', {
              text: 'Error al generar el PDF de la entrega',
              color: 'error'
            });
          }
        })
        .catch(error => {
          console.error('Error al descargar PDF:', error);
          this.$store.commit('showSnackbar', {
            text: 'Error al descargar PDF de la entrega',
            color: 'error'
          });
        });
    },
     updateNow() {
      this.currentTime = new Date();
    },

    /**
     * Format remaining time for display
     */
    formatTimeRemaining(remainingMs) {
      if (remainingMs <= 0) return 'TIEMPO VENCIDO';
      
      const totalHours = Math.floor(remainingMs / (60 * 60 * 1000));
      const minutes = Math.floor((remainingMs % (60 * 60 * 1000)) / (60 * 1000));
      const seconds = Math.floor((remainingMs % (60 * 1000)) / 1000);
      
      if (totalHours > 0) {
        return `${totalHours}h ${minutes}m ${seconds}s`;
      } else {
        return `${minutes}m ${seconds}s`;
      }
    },

    /**
     * Handle countdown expiration
     */
    onCountdownExpired() {
      // Only show notification once
      if (!this.hasShownExpiredNotification) {
        this.showErrorMessage('El tiempo de revisión ITO ha expirado', 'Tiempo Vencido');
        this.hasShownExpiredNotification = true;
        
        // Optional: Emit event for parent components
        this.$emit('countdown-expired', {
          entregaId: this.currentEntrega.id,
          expiredAt: new Date()
        });
      }
    },

    /**
     * Format business hours for display
     */
		formatBusinessHours() {
			const schedule = this.currentConfig?.work_schedule || [];
			if (!schedule.length) return '—';
			// Show the range from the first working day as a representative label
			const first = schedule[0];
			return `${first.start_working_hour} - ${first.end_working_hour}`;
		},
    
    // Helper methods for formatting
    formatDateTime(dateTime) {
      if (!dateTime) return 'N/A';
      return new Date(dateTime).toLocaleString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    
    formatTime(dateTime) {
      if (!dateTime) return 'N/A';
      return new Date(dateTime).toLocaleString('es-ES', {
        hour: '2-digit',
        minute: '2-digit',
        day: '2-digit',
        month: '2-digit'
      });
    },
    
    loadConfig() {
      if (!this.$store.entregaStore.state.config) {
        const projectId = this.$store.projectsStore.state.selectedProject.id;
        this.$store.entregaStore.dispatch('getConfig', projectId);
      }
      
      if (this.$store.usersStore.state.usersList.length === 0) {
        const projectId = this.$store.projectsStore.state.selectedProject.id;
        this.$store.usersStore.dispatch('getUsersList', { project_id: projectId });
      }
    },
    
    onButtonClick() {
      this.$refs.uploader.click()
    },
      
    async loadPlanoData(planoId) {
      try {
        await this.$store.entregaStore.dispatch('getActivePlano', { plano_id: planoId });
      } catch (error) {
        console.error('Error loading plano data:', error);
        this.showErrorMessage('Error al cargar la información del plano');
      }
    },
    
    getZonaName(zonaId) {
      if (!zonaId) return 'N/A';
      
      const config = this.$store.entregaStore.state.config || {};
      const zonas = config.zonas || [];
      
      const zona = zonas.find(z => z.id === zonaId);
      return zona ? zona.name : 'N/A';
    },
    
    getMissingRequirementsText() {
      const checks = [];
      
      if (this.approvedTasksCount < this.totalTasks) {
        checks.push(`Faltan ${this.totalTasks - this.approvedTasksCount} tareas por aprobar`);
      }
      
      if (this.currentApproversCount < this.minApprovers) {
        checks.push(`Se requieren al menos ${this.minApprovers} aprobadores (actual: ${this.currentApproversCount})`);
      }
      
      if (this.hasSupervisor && !this.currentEntrega?.supervisor_approval) {
        //checks.push('Falta aprobación del supervisor');
      }
      
      return checks.join(' • ');
    },
    
    isTaskApproved(task) {
      if (!task.approvals || task.approvals.length === 0) return false;
      
      // Agrupar por subject_id y obtener el último approval (mayor id) de cada uno
      const latestBySubject = {};
      task.approvals.forEach(approval => {
        const subjectId = approval.subject_id;
        if (!latestBySubject[subjectId] || approval.id > latestBySubject[subjectId].id) {
          latestBySubject[subjectId] = approval;
        }
      });
      
      // Contar cuántos subjects tienen 'Aprobado'
      const approvedCount = Object.values(latestBySubject).filter(approval => approval.estado === 'Aprobado').length;
      
      return approvedCount >= this.minApprovers;
    },

    isTaskItoApproved(task) {
      if (!task.approvals || task.approvals.length === 0) return false;
      
      // Obtener todos los approvals para ITO (subject_id == 4)
      const itoApprovals = task.approvals.filter(a => a.subject_id == 4);
      if (itoApprovals.length === 0) return false;
      
      // Obtener el último (mayor id)
      const latestItoApproval = itoApprovals.reduce((latest, current) => 
        current.id > latest.id ? current : latest
      );
      
      return latestItoApproval.estado === 'Aprobado';
    },
    
    isTaskAutocontrolApproved(task) {
      return task.autocontrol
    },
    
    getTaskApprovalColor(task) {
      if (this.isTaskApproved(task)) return '#3EBFA3';
      if (task.autocontrol) return '#fb8c00';
      return '#F4516C';
    },
    
    getTaskApprovalText(task) {
      if (this.isTaskApproved(task)) return 'Firmado';
      if (task.autocontrol) return 'Autocontrol';
      return 'Pendiente';
    },
    
    getApproverName(task) {
      if (!this.isTaskApproved(task)) return 'N/A';
      const approval = task.approvals[0];
      return approval.user_name || 'Usuario';
    },
    
    getApproverAvatar(task) {
      if (!this.isTaskApproved(task)) return '';
      const approval = task.approvals[0];
      return approval.user_avatar || '';
    },
    
    getStatusColor(status) {
      switch (status) {
        case 1: return '#5E85EF'; // Pendiente
        case 2: return '#fb8c00'; // En proceso
        case 3: return '#3EBFA3'; // Finalizado
        case 4: return '#000'; // Finalizado
        default: return '#757575'; // Default
      }
    },
    
	getStatusText() {
		const entrega = this.currentEntrega;
		if (!entrega) return "PREPARADO";

    // const config = this.$store.entregaStore.state.config || { minimo: 0 };
		if(this.currentEntrega.status == 4){
			return "Cerrado";
		}
		if (
			this.currentEntrega.status == 3 &&
			entrega.autocontrol
		) {
			return "Aprobado A/C TL";
		}

		if(this.approvedTasksITO + this.approvedTasksAutocontrol  >= (entrega.tasks?.length || 0) && this.currentEntrega.status == 3){
				return "Aprobado A/C"
		}

		if(this.approvedTasksITO  >= (entrega.tasks?.length || 0) && this.currentEntrega.status == 3){
				return "Aprobado"
		}

		if (this.approvedTasksCount >= (entrega.tasks?.length || 0)) {
			return "Rev. ITO";
		}

		const tasksWithApprovals = entrega.tasks?.filter(
			(task) => (task.approvals && task.approvals.length > 0) || task.autocontrol
		).length || 0;

		if (tasksWithApprovals > 0) {
			return "EN REVISIÓN";
		}

		return "PREPARADO";
	},
    
    // New autocontrol methods
    async enableAutocontrol(task) {
      if (this.loadingAutocontrol) return;
      
      this.loadingAutocontrol = task.id;
      
      try {
        const response = await setTaskApproval({
          entrega_id: this.currentEntrega.id,
          task_id: task.id,
          subject_id: task.subject_id || null,
          estado: 'autocontrol',
          comentario: 'Autocontrol habilitado por ITO'
        });
        
        if (response.code === 200) {
          const updatedTask = {...task, autocontrol: true};
          
          const taskIndex = this.currentEntrega.tasks.findIndex(t => t.id === task.id);
          if (taskIndex !== -1) {
            const updatedTasks = [...this.currentEntrega.tasks];
            updatedTasks[taskIndex] = updatedTask;
            
            const updatedEntrega = {...this.currentEntrega, tasks: updatedTasks};
            this.$store.entregaStore.commit('setCurrentEntrega', updatedEntrega);
          }
          
          if (this.taskDetailDialog && this.selectedTask.id === task.id) {
            this.selectedTask = updatedTask;
          }
          
          this.showSuccessMessage('Autocontrol habilitado correctamente');
        }
      } catch (error) {
        console.error('Error enabling autocontrol:', error);
        this.showErrorMessage('Error al habilitar autocontrol');
      } finally {
        this.loadingAutocontrol = null;
      }
    },
    
    async enableAutocontrolTotal() {
      if (this.loadingAutocontrolTotal) return;
      
      this.loadingAutocontrolTotal = true;
      
      try {
        const tasksToUpdate = this.currentEntrega.tasks.filter(task => !task.autocontrol);
        
        if (tasksToUpdate.length === 0) {
          this.showSuccessMessage('Todas las tareas ya tienen autocontrol habilitado');
          this.loadingAutocontrolTotal = false;
          return;
        }
        
        const updatePromises = tasksToUpdate.map(task => 
          setTaskApproval({
            entrega_id: this.currentEntrega.id,
            task_id: task.id,
            subject_id: task.subject_id || null,
            estado: 'autocontrol',
            comentario: 'Autocontrol total habilitado por ITO'
          })
        );
        
        const responses = await Promise.all(updatePromises);
        const failedResponse = responses.find(response => response.code !== 200);
        if (failedResponse) {
          this.showErrorMessage(failedResponse.message || 'No estás autorizado para habilitar autocontrol total');
          return;
        }
        
        const updatedTasks = this.currentEntrega.tasks.map(task => 
          task.autocontrol ? task : {...task, autocontrol: true}
        );
        
        const updatedEntrega = {...this.currentEntrega, tasks: updatedTasks};
        this.$store.entregaStore.commit('setCurrentEntrega', updatedEntrega);
        
        this.showSuccessMessage('Autocontrol total habilitado correctamente');
      } catch (error) {
        console.error('Error enabling autocontrol for all tasks:', error);
        this.showErrorMessage('Error al habilitar autocontrol total');
      } finally {
        this.loadingAutocontrolTotal = false;
      }
    },
    
    openTaskDetail(task) {
      this.selectedTask = task;
      this.taskComment = '';
      this.taskDetailDialog = true;
    },
    
    async approveTask(task, comment = '') {
      if (this.loadingApproval) return;
      
      this.loadingApproval = task.id;
      
      try {
        const response = await setTaskApproval({
          entrega_id: this.currentEntrega.id,
          task_id: task.id,
          subject_id: task.subject_id || null,
          estado: 'aprobado',
          comentario: comment || ''
        });
        
        if (response.code === 200) {
          const updatedTask = {...task};
          
          if (!updatedTask.approvals) updatedTask.approvals = [];
          updatedTask.approvals.push({
            user_id: this.$store.userStore.state.userInfo.id,
            user_name: this.$store.userStore.state.userInfo.name,
            user_avatar: this.$store.userStore.state.userInfo.info?.avatar,
            approved_at: new Date().toISOString(),
            comment: comment || ''
          });
          
          const taskIndex = this.currentEntrega.tasks.findIndex(t => t.id === task.id);
          if (taskIndex !== -1) {
            const updatedTasks = [...this.currentEntrega.tasks];
            updatedTasks[taskIndex] = updatedTask;
            
            const updatedEntrega = {...this.currentEntrega, tasks: updatedTasks};
            this.$store.entregaStore.commit('setCurrentEntrega', updatedEntrega);
          }
          
          if (this.taskDetailDialog) {
            this.taskDetailDialog = false;
          }
          
          this.showSuccessMessage('Tarea firmada correctamente');
        } else {
          this.showErrorMessage(response.message || 'No estás autorizado para firmar la tarea');
        }
      } catch (error) {
        console.error('Error approving task:', error);
        this.showErrorMessage('Error al firmar la tarea');
      } finally {
        this.loadingApproval = null;
      }
    },
    
    // Enhanced file upload methods
    // async onFileChanged(e) {
    //   const newFile = e.target.files[0];
    //   if (!newFile) return;
      
    //   this.newFiles.push(newFile);
    //   e.target.value = '';
    // },
    async onFilesChanged(e) {
      const files = e?.target?.files || []
      for (let i = 0; i < files.length; i++) {
        await this.onFileChanged(null, 20, files[i])
      }
      if (e?.target) e.target.value = ''
    },
    async onFileChanged(e, type = 20, file = null) {
      const newFile = e?.target?.files[0] || file || (typeof File !== 'undefined' && e instanceof File ? e : null)
      if (!newFile) return
			if(type == 20){
				this.newFiles.push(newFile);
				if (e?.target) e.target.value = ''
			}
			
      this.$store.globalStore.commit('setFilesToUploadCenter', {
        type,
        payload: { entrega_id: this.currentEntrega.id, plano_entrega_id: this.currentEntrega.plano_entrega?.id, project_id: this.currentEntrega.project_id },
        description: this.currentEntrega.title,
        total: newFile.size,
        uuid: uuidv4(),
        file: newFile
      })
    },
    
    removeNewAttachment(index) {
      this.newFiles.splice(index, 1);
    },
    
    async uploadAttachments() {
      if (this.loadingUpload || this.newFiles.length === 0) return;
      
      this.loadingUpload = true;
      
      try {
        const formData = new FormData();
        formData.append('entrega_id', this.currentEntrega.id);
        
        this.newFiles.forEach(file => {
          formData.append('files[]', file);
        });
        
        const response = await addInteraction(formData);
        
        if (response.code === 200) {
          this.getEntregaInfo()
          
          this.newFiles = [];
          this.showSuccessMessage('Archivos adjuntos subidos correctamente');
        }
      } catch (error) {
        console.error('Error uploading attachments:', error);
        this.showErrorMessage('Error al subir los archivos adjuntos');
      } finally {
        this.loadingUpload = false;
      }
    },
    
    // Original EntregaModal methods
    async approveSupervisor() {
      if (this.loadingSupervisorApproval) return;
      
      this.loadingSupervisorApproval = true;
      
      try {
        const response = await approveSupervisor({
          id: this.currentEntrega.id,
          supervisor_approval: true
        });
        
        if (response.code === 200) {
          const updatedEntrega = {...this.currentEntrega, supervisor_approval: true};
          this.$store.entregaStore.commit('setCurrentEntrega', updatedEntrega);
          this.showSuccessMessage('Aprobado como supervisor correctamente');
        }
      } catch (error) {
        console.error('Error approving as supervisor:', error);
        this.showErrorMessage('Error al aprobar como supervisor');
      } finally {
        this.loadingSupervisorApproval = false;
      }
    },
    
    async approveFinal() {
      if (this.loadingFinalApproval) return;
      
      this.loadingFinalApproval = true;
      
      try {
        const response = await closeEntrega({
          entrega_id: this.currentEntrega.id
        });
        
        if (response.code === 200) {
          const updatedEntrega = {
            ...this.currentEntrega, 
            status: 4,
            ended_at: new Date().toISOString()
          };
          this.$store.entregaStore.commit('setCurrentEntrega', updatedEntrega);
          
          this.showSuccessMessage('Entrega firmada y finalizada correctamente');
          this.need_reload = true;
        } else {
          this.showErrorMessage(response.message || 'No estás autorizado para finalizar la entrega');
        }
      } catch (error) {
        console.error('Error giving final approval:', error);
        this.showErrorMessage('Error al firmar la entrega');
      } finally {
        this.loadingFinalApproval = false;
      }
    },
    
    setAprobacion(approvalData) {
			console.log('Setting aprobacion with data:', approvalData);
      this.aprobacion = {
        subjectId: approvalData.subjectId,
        subject: approvalData.subject || null,
        entrega_id: this.currentEntrega.id,
        task_id: approvalData.taskId,
        taskName: approvalData.taskName,
        estado: approvalData.estado || 'Aprobado',
        comentario: approvalData.comentario || '',
				attachment: approvalData.attachment || null
      };
    },
    
    showHistory(taskId) {
      this.historyModal = {
        show: true,
        taskId
      };
    },
    
    closeHistoryModal() {
      this.historyModal.show = false;
    },
    
    setupEventListeners() {
      EventBus.$on('completedFileUpload', this.handleFileUploadComplete);
    },
    
    cleanupEventListeners() {
      EventBus.$off('completedFileUpload', this.handleFileUploadComplete);
      const textEditor = document.querySelector('#interaction-texteditor .ProseMirror');
      if (textEditor) {
        textEditor.removeEventListener('paste', this.handlePaste);
      }
    },
    
    async handleFileUploadComplete(payload) {
      if (payload && payload.entrega_id && this.currentEntrega && payload.entrega_id === this.currentEntrega.id) {
        try {
          this.getEntregaInfo();
        } catch (error) {
          console.error('Error refreshing entrega data:', error);
        }
      }
    },
    
    closeModal() {
      const reload = this.need_reload || !this.currentEntrega.prev_seen_by_me;
      this.$store.entregaStore.commit('setCurrentEntrega', null);
      
      if (reload) {
        this.getEntregaList();
      }
      
      this.resetState();
    },
    
    resetState() {
      this.need_reload = false;
      this.loading = false;
      this.files = [];
      this.filesPreview = [];
      this.newFiles = [];
      this.hasShownExpiredNotification = false;
      
      // Stop time updates when modal closes
      // this.stopTimeUpdate();
    },
    
    async getEntregaList() {
      let project_id = null;
      
      if (this.$store.projectsStore.state.selectedProject) {
        project_id = this.$store.projectsStore.state.selectedProject.id;
      } else {
        const id = localStorage.getItem('selectedProjectID');
        project_id = parseInt(id);
      }
      
      if (project_id) {
        this.$store.entregaStore.dispatch('getEntrega', { project_id });
      }
    },

    previewAttachment(attachment) {
      const normalized = normalizeAttachmentForPreview(attachment)
      this.closeAttachmentViewer()

      if (normalized?.pathmedia?.path) {
        const mediaItem = toMediaViewerItem(normalized)
        if (mediaItem) {
          this.viewerMediaItems = [mediaItem]
          this.viewerMediaIndex = 0
          return
        }
      }

      if (normalized?.pathdoc?.path) {
        this.viewerDocumentItem = normalized
        return
      }

      if (normalized?.file_type === 'forge' || normalized?.forge_file?.translated) {
        this.viewerForgeItem = normalized
        return
      }

      this.showErrorMessage('Este archivo no tiene un visor disponible', 'Vista previa no disponible')
    },

    closeAttachmentViewer() {
      this.viewerMediaItems = []
      this.viewerMediaIndex = null
      this.viewerDocumentItem = null
      this.viewerForgeItem = null
    },

    minimizeAttachmentViewer(item) {
      if (!item) return
      this.$store.globalStore.commit('addFileToFileManagerHandler', item)
      this.closeAttachmentViewer()
    },

    onViewerError() {
      this.showErrorMessage('No fue posible cargar el archivo', 'Error de preview')
    },

    onViewerLoad() {},
    
    async getFilesUrl(attachment, download = false) {
      this.loadingFilePath = true;
      
      try {
        const files = await getPublicUrl({ 
          attachments: [attachment], 
          force_download: download 
        });
        
        if (files.code === 200) {
          files.files.forEach(file => {
            window.open(file.url);
          });
        }
      } catch (error) {
        console.error('Error getting file URL:', error);
        this.showErrorMessage('Error al obtener la URL del archivo');
      } finally {
        this.loadingFilePath = false;
      }
    },
    
    downloadAttachment(attachment) {
      this.getFilesUrl(attachment, true);
    },
    
    async downloadAllAttachments() {
      this.loadingFilePath = true;
      
      try {
        const files = await getZippedFiles({ files_to_zip: this.currentEntrega.attachments });
        
        if (files.code === 200) {
          window.open(files.fileurl);
        }
      } catch (error) {
        console.error('Error downloading attachments:', error);
        this.showErrorMessage('Error al descargar los archivos');
      } finally {
        this.loadingFilePath = false;
      }
    },
    
    toBase64(file) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => resolve(null);
      });
    },
    
    showErrorMessage(message, title = 'Error') {
      const data = {
        message,
        title,
        created: new Date(),
        type: 'error',
        icon: 'mdi-alert-circle',
        color: '#fb8c00'
      };
      
      this.$store.globalStore.dispatch('addMessage', data);
    },
    
    showSuccessMessage(message, title = 'Éxito') {
      const data = {
        message,
        title,
        created: new Date(),
        type: 'success',
        icon: 'mdi-check-circle',
        color: '#3EBFA3'
      };
      
      this.$store.globalStore.dispatch('addMessage', data);
    },
    
    async viewRdi(rdi_id, seen_by_me) {
      this.$store.rdiStore.commit('setCurrentRDI', { loading: true });
      
      try {
        const response = await getRdiByID({ rdi_id });
        
        if (response.code === 200) {
          const rdiData = {
            ...response.rdi,
            prev_seen_by_me: seen_by_me
          };
          
          this.$store.rdiStore.commit('setCurrentRDI', rdiData);
        }
      } catch (error) {
        console.error('Error fetching RDI:', error);
        this.showErrorMessage('Error al cargar la RDI');
      }
    },
    
    async viewTask(task_id, seen_by_me) {
      this.$store.consultasStore.commit('setCurrentTarea', { loading: true });
      
      try {
        const response = await getTareaByID({ task_id });
        
        if (response.code === 200) {
          const tareaData = {
            ...response.task,
            prev_seen_by_me: seen_by_me
          };
          
          this.$store.consultasStore.commit('setCurrentTarea', tareaData);
        }
      } catch (error) {
        console.error('Error fetching task:', error);
        this.showErrorMessage('Error al cargar la tarea');
      }
    },
    
    async viewAdicional(adicional_id, seen_by_me) {
      console.log('View adicional', adicional_id, seen_by_me);
    },
    
    closeModalAcceptCancel(status = false) {
      this.acceptRejectModal = false;
      
      if (!status || !this.acceptRejectModalData) {
        this.acceptRejectModalData = null;
        return;
      }
      
      if (this.acceptRejectModalData.type === 1) {
        this.closeEntrega();
      }
			else if (this.acceptRejectModalData.type === 2) {
        this.deleteRdi();
      }
			else if (this.acceptRejectModalData.type === 3) {
				this.deleteCurrentEntrega();
			}
      
      this.acceptRejectModalData = null;
    },
    
    showPasswordModal() {
      this.passwordModal = true;
    },
    
    async handlePasswordConfirm(password) {
      this.verifyingPassword = true;
      
      try {
        const response = await this.verifyPassword(password);
        
        if (response.success) {
          await this.finalizeEntrega();
        } else {
          this.$refs.passwordModal.errorMessage = 'La contraseña ingresada es incorrecta';
        }
      } catch (error) {
        console.error('Error verifying password:', error);
        this.$refs.passwordModal.errorMessage = 'Error al verificar la contraseña';
      } finally {
        this.verifyingPassword = false;
      }
    },
    
    async verifyPassword(password) {
      try {
        const data = {
          password: password,
          user_id: this.$store.userStore.state.userInfo.id
        };
        
        const response = await verifyUserPassword(data);
        
        if (response.code === 200) {
          return { success: true };
        } else {
          return { success: false, error: response.message || 'Contraseña incorrecta' };
        }
      } catch (error) {
        console.error('Error verifying password:', error);
        return { success: false, error: 'Error al verificar contraseña' };
      }
    },
    
    async finalizeEntrega() {
      try {
        const response = await closeEntrega({ entrega_id: this.currentEntrega.id });
        
        if (response.code === 200) {
					this.getEntregaInfo();
          
          this.$store.notificationsStore.dispatch('getReminders');
          this.$store.projectsStore.dispatch('getDashboardInfo');
          
          this.need_reload = true;
          this.showSuccessMessage('La entrega ha sido firmada y cerrada correctamente', 'Entrega Finalizada');
          
          this.passwordModal = false;
        }
      } catch (error) {
        console.error('Error finalizing entrega:', error);
        this.showErrorMessage('Error al finalizar la entrega');
      }
    },
    
    startCloseEntrega() {
      this.acceptRejectModalData = {
        title: `¿Cerrar la Entrega Nº ${this.currentEntrega.number}?`,
        content_a: 'Por favor confirma si deseas cerrar la Entrega indicada.',
        type: 1
      };
      
      this.acceptRejectModal = true;
    },
    
    async closeEntrega() {
      try {
        await this.beforeAddInteraction(true);
        const response = await closeEntrega({ entrega_id: this.currentEntrega.id });
        
        if (response.code === 200) {
          this.$store.entregaStore.commit('setCurrentEntrega', {
            ...this.currentEntrega,
            status: 4
          });
          
          this.$store.notificationsStore.dispatch('getReminders');
          this.$store.projectsStore.dispatch('getDashboardInfo');
          
          this.need_reload = true;
          this.message = '';
          this.files = [];
          this.filesPreview = [];
        }
      } catch (error) {
        console.error('Error closing entrega:', error);
        this.showErrorMessage('Error al cerrar la entrega');
      }
    },

    minimizeItem(){
      const data = {
        task_id: this.currentEntrega.id,
        title: this.currentEntrega.title,
        type: 'Entrega',
        type_id: 7,
        number: this.currentEntrega.number || this.currentEntrega.id
      }
      this.$store.globalStore.commit('addTaskToTasksHandler', data)
      this.closeModal()
    },
    
    startDeleteRdi() {
      this.acceptRejectModalData = {
        title: `¿Eliminar la Entrega Nº ${this.currentEntrega.number}?`,
        content_a: 'Por favor confirma si deseas eliminar la Entrega indicada.',
        type: 2
      };
      
      this.acceptRejectModal = true;
    },
    
    async deleteRdi() {
      try {
        const response = await deleteRdi({ task_id: this.currentEntrega.id });
        
        if (response.code === 200) {
          this.getEntregaList();
          this.$store.notificationsStore.dispatch('getReminders');
          this.$store.entregaStore.commit('setCurrentEntrega', null);
          
          this.showSuccessMessage('Se ha eliminado correctamente la Entrega', 'Entrega eliminada');
        }
      } catch (error) {
        console.error('Error deleting RDI:', error);
        this.showErrorMessage('Error al eliminar la entrega');
      }
    },
    
    async beforeAddInteraction(closing = false) {
      if (!closing && (this.message === '' || !this.message)) {
        this.showErrorMessage('Por favor escribe un mensaje. Este campo no puede estar vacío.');
        return false;
      }
      
      const promises = [];
      
      if (closing && (this.message === '' || !this.message)) {
        promises.push(
          this.addInteraction(
            this.currentEntrega, 
            'Entrega cerrada por usuario', 
            false, 
            []
          )
        );
      } else {
        promises.push(
          this.addInteraction(
            this.currentEntrega, 
            this.message, 
            this.is_response, 
            this.files
          )
        );
        
        if (closing) {
          promises.push(
            this.addInteraction(
              this.currentEntrega, 
              'Entrega cerrada por usuario', 
              false, 
              []
            )
          );
        }
      }
      
      try {
        await Promise.all(promises);
        return true;
      } catch (error) {
        console.error('Error adding interaction:', error);
        return false;
      }
    },
    
    async addInteraction(entrega, message, is_response, files) {
      this.loading = true;
      
      try {
        const biggerFiles = [];
        const formData = new FormData();
        
        formData.append('entrega_id', entrega.id);
        formData.append('message', message);
        formData.append('is_response', is_response);
        
        files.forEach(file => {
          if (file && file.size < 1000) {
            formData.append('files[]', file);
          } else if (file) {
            biggerFiles.push({
              type: 4,
              payload: { 
                'rdi_id': entrega.id, 
                'project_id': this.$store.projectsStore.state.selectedProject.id 
              },
              description: entrega.title,
              total: file.size,
              uuid: uuidv4(),
              file
            });
          }
        });
        
        const response = await addInteraction(formData);
        
        if (response.code === 200) {
          if (biggerFiles.length > 0) {
            biggerFiles.forEach(file => {
              file.payload = { 
                ...file.payload, 
                rdi_interaction_id: response.interaction,
                project_id: this.$store.projectsStore.state.selectedProject.id
              };
              
              this.$store.globalStore.commit('setFilesToUploadCenter', file);
            });
          }
          
          this.need_reload = true;
          this.message = '';
          this.files = [];
          this.filesPreview = [];
          
          this.$store.entregaStore.commit('setCurrentEntrega', response.rdi);
          return true;
        }
        
        return false;
      } catch (error) {
        console.error('Error adding interaction:', error);
        this.showErrorMessage('Error al añadir la interacción');
        return false;
      } finally {
        this.loading = false;
      }
    },
    
    closeAprobacionModal() {
      this.aprobacion = null;
    },
    
    closeSendEmailModal() {
      this.sendByEmailModal = false;
    },
    
    async savedTask() {
      try {
				this.getEntregaInfo();
        
        this.aprobacion = null;
      } catch (error) {
        console.error('Error refreshing entrega:', error);
        this.showErrorMessage('Error al actualizar la entrega');
      }
    }
  }
};
</script>
  
<style scoped>
/* ═══════════════════════════════════════════════
   LAYOUT PRINCIPAL — estilo TareaModal
═══════════════════════════════════════════════ */

.entrega-modal-wrapper { display: contents; }

/* Dialog full-height */
:deep(.entrega-dialog) {
  display: flex;
  flex-direction: column;
  height: 90vh;
  max-height: 90vh;
}
:deep(.entrega-dialog .v-card.modal-card) {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  border-radius: 12px;
}

/* ── LOADING CARD ── */
.loading-card {
  padding: 0;
}
.loading-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  background: #F4F6FB;
  border-bottom: 1px solid #E5E8EF;
}
.modal-brand {
  display: flex;
  align-items: center;
  gap: 8px;
}
.brand-icon-dark {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: #EEF2FF;
  border: 1px solid #C7D2FE;
}
.brand-label-dark {
  font-size: 14px;
  font-weight: 600;
  color: #1A1D23;
}
.close-btn-dark {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: transparent;
  border: 1px solid #E5E8EF;
  cursor: pointer;
  transition: background 0.15s;
}
.close-btn-dark:hover { background: #F3F4F6; }
.loading-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  gap: 16px;
}
.loading-text {
  color: #6B7280;
  font-size: 14px;
  margin: 0;
}

/* ── TOP BAR ── */
.topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: #F4F6FB;
  border-bottom: 1px solid #E5E8EF;
  flex-shrink: 0;
  gap: 12px;
}
.topbar-left {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  overflow: hidden;
}
.topbar-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 6px;
  background: #EEF2FF;
  border: 1px solid #C7D2FE;
  flex-shrink: 0;
  margin-top: 2px;
}
.topbar-titles {
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow: hidden;
}
.topbar-title {
  font-size: 15px;
  font-weight: 700;
  color: #1A1D23;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.topbar-number {
  font-size: 13px;
  font-weight: 600;
  color: #5E85EF;
  margin-left: 6px;
}
.topbar-rdi {
  font-size: 12px;
  color: #6B7280;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  gap: 4px;
}
.topbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.topbar-action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: opacity 0.15s;
}
.topbar-action-btn:hover { opacity: 0.85; }
.minimize-btn { background: #fb8c00; }
.close-btn-topbar { background: #F4516C; }

/* ── INLINE BUTTONS ── */
.inline-link-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 4px;
  background: #EEF2FF;
  border: 1px solid #C7D2FE;
  cursor: pointer;
  padding: 0;
  vertical-align: middle;
  transition: background 0.15s;
}
.inline-link-btn:hover { background: #C7D2FE; }

.inline-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 5px;
  font-size: 12px;
  font-weight: 500;
  color: #5E85EF;
  background: #EEF2FF;
  border: 1px solid #C7D2FE;
  cursor: pointer;
  transition: background 0.15s;
}
.inline-action-btn:hover { background: #C7D2FE; }

/* ── STATUS STRIP ── */
.status-strip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 20px;
  background: #FAFBFF;
  border-bottom: 1px solid #E5E8EF;
  flex-shrink: 0;
  gap: 8px;
  flex-wrap: wrap;
}
.status-strip-pills {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}
.status-strip-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 9px;
  border-radius: 20px;
  font-size: 11.5px;
  font-weight: 500;
  border: 1px solid transparent;
  white-space: nowrap;
}
.pill--blue   { background: #EEF2FF; color: #2D4B95; border-color: #C7D2FE; }
.pill--green  { background: #ECFDF5; color: #1B7A4E; border-color: #A7F3D0; }
.pill--orange { background: #FFF7ED; color: #B45309; border-color: #FED7AA; }
.pill--gray   { background: #F3F4F6; color: #555;    border-color: #D1D5DB; }

/* ── MODAL BODY: DOS PANELES ── */
.modal-body {
  display: flex;
  flex: 1;
  overflow: hidden;
  min-height: 0;
}
.modal-body.resizing {
  cursor: col-resize;
  user-select: none;
}

.panel-detail,
.panel-approvals {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
}

.panel-detail {
  border-right: 1px solid #E5E8EF;
}

.panel-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 20px 20px 32px;
  height: 100%;
}

/* ── PANEL RESIZER ── */
.panel-resizer {
  width: 12px;
  background: #F3F4F6;
  border-left: 1px solid #E5E8EF;
  border-right: 1px solid #E5E8EF;
  cursor: col-resize;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s;
  position: relative;
}
.panel-resizer:hover { background: #E5E8EF; }
.resizer-handle {
  width: 3px;
  height: 36px;
  border-radius: 3px;
  background: #C4C9D4;
}
.panel-resizer:hover .resizer-handle { background: #2D4B95; }

/* ── META GRID ── */
.meta-section {
  margin-bottom: 20px;
}
.meta-two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  align-items: start;
}
.meta-left {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.meta-right {
  padding-top: 2px;
}
.meta-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.meta-label {
  font-size: 11px;
  font-weight: 600;
  color: #6B7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 3px;
}
.meta-value {
  font-size: 13px;
  color: #1A1D23;
  display: flex;
  align-items: center;
  gap: 5px;
  flex-wrap: wrap;
}
.meta-value--link {
  color: #2D4B95;
}

/* ── SECCIONES ── */
.content-section {
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #F0F2F7;
}
.content-section:last-child {
  border-bottom: none;
}
.section-title {
  font-size: 13px;
  font-weight: 700;
  color: #2D4B95;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
}
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.description-body {
  font-size: 13px;
  color: #374151;
  line-height: 1.65;
}

/* Visor de plano embebido */
.plano-viewer-container {
  min-height: 300px;
  height: 320px;
  border: 1px solid #E5E8EF;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

/* ── DESCRIPCIÓN + SUPERVISOR ── */
.desc-supervisor-row {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}
.desc-col {
  flex: 3;
  min-width: 0;
}
.supervisor-col {
  flex: 1;
  min-width: 120px;
}
.three-col-row {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}
.three-col-row.three-col-row--equal .desc-col,
.three-col-row.three-col-row--equal .info-col,
.three-col-row.three-col-row--equal .supervisor-col {
  flex: 1;
  min-width: 0;
}

/* Fallback: original proportions preserved for other usages */
.three-col-row .desc-col { flex: 2; min-width: 0 }
.three-col-row .info-col { flex: 1; min-width: 180px }
.three-col-row .supervisor-col { flex: 1; min-width: 160px }
.three-col-row.three-col-row--equal .meta-label {
  color: #2D4B95;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.supervisor-col .meta-label {
  color: #2D4B95;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.plano-full-row { width: 100%; }
.supervisor-card-compact {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 12px 8px;
  border-radius: 8px;
  background: #F8F9FC;
  border: 1px solid #E5E8EF;
}
.supervisor-name-sm {
  font-size: 12px;
  font-weight: 600;
  color: #1A1D23;
  margin-bottom: 2px;
}
.supervisor-role-sm {
  font-size: 10px;
  color: #6B7280;
}

/* ── SUPERVISOR CARD (legacy, keep for compat) ── */
.supervisor-card {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 12px;
  border-radius: 8px;
  background: #F8F9FC;
  border: 1px solid #E5E8EF;
}
.supervisor-name {
  font-size: 13px;
  font-weight: 600;
  color: #1A1D23;
}
.supervisor-role {
  font-size: 11px;
  color: #6B7280;
}
.supervisor-info {
  flex: 1;
}

/* ── APPROVAL PROGRESS ── */
.approval-progress-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
.approval-progress-item {
  display: flex;
  flex-direction: column;
}
.approval-progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: #374151;
  margin-bottom: 2px;
}
.missing-requirements-text {
  font-size: 12px;
  color: #F4516C;
  text-align: center;
  margin-top: 8px;
}

/* ── FILE UPLOAD ── */
.upload-trigger-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  color: #2D4B95;
  background: #EEF2FF;
  border: 1px solid #C7D2FE;
  cursor: pointer;
  transition: background 0.15s;
}
.upload-trigger-btn:hover { background: #C7D2FE; }
.upload-toggle-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  color: #2D4B95;
  background: #EEF2FF;
  border: 1px solid #C7D2FE;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.plano-actions { gap: 10px; }
.upload-toggle-btn:hover { background: #C7D2FE; }
.upload-toggle-btn--active {
  background: #2D4B95;
  color: #fff;
  border-color: #2D4B95;
}
.upload-form-area {
  border-top: 1px solid #E5E8EF;
  margin-top: 12px;
  padding-top: 12px;
}
.upload-form-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}
.new-file-row {
  display: flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid #E5E8EF;
  margin-bottom: 4px;
  background: #F8F9FC;
  font-size: 12px;
}
.new-file-name {
  flex: 1;
  color: #374151;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.remove-file-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 2px;
  border-radius: 4px;
}
.remove-file-btn:hover { background: #FEE2E2; }
.upload-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  border-radius: 8px;
  background: #F8F9FC;
  border: 1px dashed #D1D5DB;
  gap: 6px;
}
.upload-empty-text {
  font-size: 12px;
  color: #9CA3AF;
  margin: 0;
}

/* ── PANEL APPROVALS ── */
.panel-approvals {
  background: #FAFBFF;
}
.approvals-header {
  display: flex;
  flex-direction: column;
  padding: 12px 16px;
  border-bottom: 2px solid #E5E8EF;
  background: #FAFBFF;
  flex-shrink: 0;
}
.approvals-header-main {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
}
.approvals-title {
  font-size: 12px;
  font-weight: 700;
  color: #1A1D23;
  letter-spacing: 0.02em;
  margin: 0;
  flex: 1;
  flex-shrink: 0;
}
.donuts-row {
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: center;
}
.donut-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}
.donut-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 45px;
  height: 45px;
  flex-shrink: 0;
}
.donut-text {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 8px;
  font-weight: bold;
  color: #1A1D23;
}
.donut-label {
  font-size: 9px;
  color: #6B7280;
  font-weight: 500;
  text-align: center;
}
.approvals-header-actions {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}

/* ── TASKS LIST ── */
.tasks-list {
  display: flex;
  flex-direction: column;
  gap: 0;
}
.tasks-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  gap: 12px;
  color: #9CA3AF;
}
.tasks-empty-text {
  font-size: 13px;
  margin: 0;
  color: #9CA3AF;
}

/* ── COUNTDOWN (mantener los existentes) ── */
.countdown-alert {
  border-left-width: 6px !important;
  transition: all 0.3s ease;
  position: relative;
}
.countdown-text {
  letter-spacing: 1px;
  text-shadow: 0 2px 4px rgba(0,0,0,0.1);
  font-size: 1.1rem;
}
.alert-pulse {
  animation: pulse 2s infinite;
}
.alert-outside-hours {
  opacity: 0.8;
  background: linear-gradient(45deg, transparent 25%, rgba(255,193,7,0.1) 25%, rgba(255,193,7,0.1) 50%, transparent 50%, transparent 75%, rgba(255,193,7,0.1) 75%);
  background-size: 20px 20px;
  animation: move-stripes 2s linear infinite;
}
.progress-paused {
  opacity: 0.7;
  animation: pulse-progress 3s ease-in-out infinite;
}
@keyframes pulse {
  0%   { box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.7); }
  70%  { box-shadow: 0 0 0 10px rgba(244, 67, 54, 0); }
  100% { box-shadow: 0 0 0 0 rgba(244, 67, 54, 0); }
}
@keyframes move-stripes {
  0%   { background-position: 0 0; }
  100% { background-position: 20px 0; }
}
@keyframes pulse-progress {
  0%, 100% { opacity: 0.7; }
  50%       { opacity: 0.9; }
}

/* ── MOBILE ── */
@media (max-width: 600px) {
  :deep(.entrega-dialog) { height: 100vh; max-height: 100vh; border-radius: 0; }
  .modal-body {
    flex-direction: column;
    overflow-y: auto;
    overflow-x: hidden;
  }
  .panel-detail,
  .panel-approvals {
    overflow: visible;
    height: auto;
    min-height: unset;
    flex-shrink: 0;
  }
  .panel-scroll {
    overflow-y: visible;
    height: auto;
    flex: none;
  }
  .approvals-header {
    position: static;
  }
  .panel-detail { border-right: none; border-bottom: 1px solid #E5E8EF; }
  .meta-flat-list { grid-template-columns: 1fr; }
  .meta-two-col { grid-template-columns: 1fr; }
  .desc-supervisor-row { flex-direction: column; }
  .three-col-row,
  .three-col-row--equal { flex-direction: column; }
  .supervisor-col { width: 100%; }
  .approval-progress-grid { grid-template-columns: 1fr; }
  .countdown-text { font-size: 0.9rem; letter-spacing: 0.5px; }
  .topbar-title { font-size: 13px; }
  .topbar-rdi { display: none; }
}

/* ── LEGACY (mantener compatibilidad) ── */
.text-primary { color: #2D4B95; }
.border-file { border: 1px solid #e0e0e0; border-radius: 4px; }
.red--text   { color: #f44336 !important; }
.orange--text { color: #ff9800 !important; }
.amber--text  { color: #ffc107 !important; }
.green--text  { color: #4caf50 !important; }
/* ── MOBILE ESPECÍFICO PARA COUNTDOWN ── */
@media (max-width: 600px) {
  .countdown-alert .d-flex { flex-direction: column; align-items: flex-start !important; }
  .countdown-alert .d-flex > div:last-child { margin-top: 10px; align-self: flex-end; }
}
/* ── APPROVAL ATTACHMENT SECTION ── */
.apv-attach-section {
  margin-top: 16px;
  border-top: 1px solid #F0F2F7;
  padding-top: 14px;
}
.apv-attach-divider {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  color: #6B7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
}
.apv-subpartida {
  margin-bottom: 12px;
}
.apv-subpartida-header {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px 8px;
  border-radius: 6px;
  background: #EEF2FF;
  margin-bottom: 8px;
}
.apv-subpartida-label {
  font-size: 12px;
  font-weight: 700;
  color: #2D4B95;
  letter-spacing: 0.2px;
}
.apv-task {
  padding-left: 12px;
  border-left: 2px solid #E5E8EF;
  margin-bottom: 10px;
  margin-left: 4px;
}
.apv-task-header {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 6px;
}
.apv-task-label {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
}
.apv-jobtype {
  padding-left: 8px;
  margin-bottom: 8px;
}
.apv-jobtype-pill {
  margin-bottom: 6px;
  display: inline-flex;
}
</style>